import React from "react";
import "./CertificateScope.css";
import { Form, Row, Col } from "react-bootstrap";
import DatePicker from "../../DatePicker";

interface CertificateScopeProps {
  formData: any;
  section34Data: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const CertificateScope: React.FC<CertificateScopeProps> = ({
  formData,
  section34Data,
  setFormData,
}) => {
  const certificates = section34Data?.opportunityCertificates || [];

  const applicableStandards: Record<string, string[]> = {
    FSC: [
      "FSC-STD-40-003 - Chain Of Custody",
      "FSC-STD-40-005 - Controlled Wood Sourcing",
      "FSC-STD-40-006-V2-0 ProjectCertification",
      "FSC-STD-40-004",
      "FSC-STD-01-004 V1-0",
      "FSC-STD-50-001 - Trademark Use",
      "FSC-STD-40-007 - Reclaimed Material",
    ],
    PEFC: ["PEFC COC/Trademark Use Std"],
    SFI: ["SFI 2022, Section 4 (Chain of Custody)"],
  };

  const scopesMap: Record<
    string,
    { title: string; note: string }
  > = {
    FSC: {
      title: "Forest Stewardship Council (FSC)",
      note:
        "This single/multi-site/group certificate covers the production/distribution/trade of products using the physical separation/percentage-based method(s). It also covers a DDS for the control of wood sourced from list the geographic region(s); sale of PEFC Controlled Sources; and sourcing of recycled material.",
    },
    PEFC: {
      title: "Programme for the Endorsement of Forest Certification (PEFC)",
      note:
        "This single/multi-site/group certificate covers the production/distribution/trade of products using the physical separation/percentage-based method(s). It also covers a DDS for the control of wood sourced from list the geographic region(s); sale of PEFC Controlled Sources; and sourcing of recycled material.",
    },
    SFI: {
      title: "Sustainable Forestry Initiative (SFI)",
      note:
        "This single/multi-site/group certificate covers the production/distribution/trade of products using the physical separation/percentage-based method(s). It also covers a DDS for the control of wood sourced from list the geographic region(s); sale of PEFC Controlled Sources; and sourcing of recycled material.",
    },
  };

  const standardMappings: Record<string, Record<string, keyof typeof formData>> = {
    FSC: {
      "FSC-STD-40-007 - Reclaimed Material": "FSC_STD_Reclaimed_Material__c",
      "FSC-STD-40-006-V2-0 ProjectCertification": "FSC_STD_Project_Certification__c",
      "FSC-STD-40-004": "FSC_STD_40_004__c",
      "FSC-STD-40-003 - Chain Of Custody": "FSC_STD_Chain_Of_Custody__c",
      "FSC-STD-50-001 - Trademark Use": "FSC_STD_Trademark_Use__c",
      "FSC-STD-01-004 V1-0": "FSC_STD_01_004_V1_0__c",
      "FSC-STD-40-005 - Controlled Wood Sourcing": "FSC_STD_Controlled_Wood_Sourcing__c",
    },
    PEFC: {
      "PEFC COC/Trademark Use Std": "PEFC_COC_Trademark_Use_Std__c",
    },
    SFI: {
      "SFI 2022, Section 4 (Chain of Custody)": "SFI_2022_Section_4_Chain_of_Custody__c",
    },
  };

  const handleChangeCertificateScope = (id: string, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      opportunityCertificates: prev.opportunityCertificates.map((item: any) => item.id === id ? {...item, scopeOfCertification: value} : item)
    }))
  }

  // Helper to get normalized program key from raw ProgramName__c
  const getProgramKey = (programRaw: string): string => {
    const prog = programRaw.toUpperCase();
    if (prog.includes("FSC")) return "FSC";
    if (prog.includes("PEFC")) return "PEFC";
    if (prog.includes("SFI")) return "SFI";
    return "";
  };

  if (!certificates.length) {
    return <div>No certificates found.</div>;
  }

  return (
    <>
      {certificates.map((certificate:any, index:any) => {
        const program = getProgramKey(certificate.ProgramName__c || "");
        if (!program || !scopesMap[program]) {
          // Skip unsupported or missing program certificate
          return null;
        }

        const leftFields = [
          {
            label: "Chain of Custody Code",
            placeholder: "SCS-COC-######",
            value: certificate.Certificate_Number__c,
          },
          {
            label: "Controlled Wood Code",
            placeholder: "SCS-CW-#####",
            value: certificate.Controlled_Wood_Number__c,
          },
          {
            label: "Trademark License Code",
            placeholder: "SCS-TL-#####",
            value: certificate.Program_Membership_Number__c,
          },
        ];

        const rightFields = [
          {
            label: "Issue Date",
            value: certificate.ValidFrom__c,
          },
          {
            label: "Expiry Date",
            value: certificate.Valid_Through__c,
          },
        ];

        return (
          <div
            key={certificate.Id || index}
            className="certificate-scope bg-white p-4 mb-4 rounded-4"
          >
            <Form.Group
              className="mb-3 d-flex align-items-center"
              controlId={`Total_Sites__c_${program}_${index}`}
            >
              <Form.Label className="me-3 mb-0">
                Total Number of Sites Included in the Certificate (excluding outsourcers) - {program}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Type here"
                className="w-15"
                name={`Total_Sites__c_${program}_${index}`}
                value={certificate.Number_of_Sites__c || ""}
                disabled
              />
            </Form.Group>

            <h5 className="certificate-scope-sectionheading">{scopesMap[program].title}</h5>

            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  {leftFields.map((field, idx) => (
                    <Row className="mb-3" key={`left-field-${program}-${index}-${idx}`}>
                      <Col md={4}>
                        <Form.Label>{field.label}</Form.Label>
                      </Col>
                      <Col md={8}>
                        <Form.Control
                          type="text"
                          placeholder={field.placeholder}
                          value={field.value || ""}
                          disabled
                        />
                      </Col>
                    </Row>
                  ))}
                </Col>

                <Col md={6}>
                  {rightFields.map((field, idx) => (
                    <Row className="mb-3" key={`right-field-${program}-${index}-${idx}`}>
                      <Col md={4}>
                        <Form.Label>{field.label}</Form.Label>
                      </Col>
                      <Col md={8}>
                        <DatePicker
                          selected={field.value}
                          disabled
                          dateFormat="d MMMM yyyy"
                        />
                      </Col>
                    </Row>
                  ))}
                </Col>
              </Row>

              <hr className="divider" />

              <Form.Label>Applicable Standards</Form.Label>
              <Row>
                {applicableStandards[program].map((standard, idx) => {
                  const fieldKey = standardMappings[program][standard];
                  const isChecked = formData?.[fieldKey] ?? false;

                  return (
                    <Col md={4} key={`${program}-standard-${index}-${idx}`}>
                      <Form.Check
                        type="checkbox"
                        label={<div dangerouslySetInnerHTML={{ __html: standard }} />}
                        id={`${program}-standard-${index}-${idx}`}
                        checked={isChecked}
                        onChange={(e) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            [fieldKey]: e.target.checked,
                          }))
                        }
                      />
                    </Col>
                  );
                })}
              </Row>

              <Row className="mt-3">
                <Col>
                <Form.Label className="mt-1">Scope of Certificate</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Add Scope of Certificate..."
                    defaultValue={certificate.Scope_of_certification__c}
                    onChange={(e) => handleChangeCertificateScope(certificate.id, e.target.value)}
                  />
                </Col>
              </Row>
            </Form>
          </div>
        );
      })}
    </>
  );
};

export default CertificateScope;
